import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AddShiftDialog,
  CalendarView,
  DayDetail,
  EditSlotDialog,
  ShiftConfigurationPanel,
  type ShiftSlotWithUser,
} from '@/components/features/shifts';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useCreateSlot,
  useDeleteRoster,
  useDeleteSlot,
  useGenerateRoster,
  useGroups,
  useShiftPatterns,
  useShiftRosters,
  useShiftSlots,
  useShifts,
  useUpdateRoster,
  useUpdateSlot,
  useUserGroups,
  useUsers,
} from '@/hooks/useShiftManagement';

export const Route = createFileRoute('/_authenticated/shift-management')({
  component: ShiftManagement,
});

function ShiftManagement() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Queries
  const { data: groups = [], isLoading: groupsLoading } = useGroups();
  const { data: shifts = [], isLoading: shiftsLoading } = useShifts();
  const { data: patterns = [], isLoading: patternsLoading } = useShiftPatterns();
  const { data: users = [], isLoading: usersLoading } = useUsers();
  const { data: userGroups = [] } = useUserGroups();
  const { data: rosters = [], isLoading: rostersLoading } = useShiftRosters();

  // Mutations
  const generateRoster = useGenerateRoster();
  const updateRoster = useUpdateRoster();
  const createSlot = useCreateSlot();
  const updateSlot = useUpdateSlot();
  const deleteSlot = useDeleteSlot();
  const deleteRoster = useDeleteRoster();

  // State
  const [selectedPatternId, setSelectedPatternId] = useState<string>('');
  const [monthCursor, setMonthCursor] = useState<Date>(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [holidays, setHolidays] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('holidays');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [newHoliday, setNewHoliday] = useState<string>('');
  const [editOpen, setEditOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<ShiftSlotWithUser | null>(null);
  const [editingShiftId, setEditingShiftId] = useState<string>('');
  const [editingUserId, setEditingUserId] = useState<string>('');
  const [openDayPopover, setOpenDayPopover] = useState<string | null>(null);
  const [addShiftOpen, setAddShiftOpen] = useState(false);
  const [addShiftDate, setAddShiftDate] = useState<string>('');
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedRosterId, setSelectedRosterId] = useState<string | null>(null);
  const allRostersSelectedRef = useRef(false); // Track if "All Rosters" was explicitly selected
  const hasInitializedRef = useRef(false); // Track if initial selection has been done

  // Selected roster - use selectedRosterId or null for "All"
  const activeRoster = useMemo(() => {
    if (allRostersSelectedRef.current || selectedRosterId === null) {
      return null; // "All Rosters" mode
    }
    if (selectedRosterId) {
      return rosters.find((r) => r.id === selectedRosterId) || null;
    }
    return null;
  }, [rosters, selectedRosterId]);

  // Helper function to format date as YYYY-MM-DD without timezone conversion
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate date range for current month view
  const monthDateRange = useMemo(() => {
    const year = monthCursor.getFullYear();
    const month = monthCursor.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0); // Last day of month

    return {
      startDate: formatDateLocal(startDate),
      endDate: formatDateLocal(endDate),
    };
  }, [monthCursor, formatDateLocal]);

  // Auto-select roster containing today and select today's date on first load
  useEffect(() => {
    if (hasInitializedRef.current || rosters.length === 0) return;

    const today = new Date();
    const todayIso = formatDateLocal(today);

    // Find roster that contains today's date
    const rosterContainingToday = rosters.find((r) => {
      const startsOn = new Date(r.startsOn);
      const endsOn = new Date(r.endsOn);
      return today >= startsOn && today <= endsOn;
    });

    if (rosterContainingToday) {
      setSelectedRosterId(rosterContainingToday.id);
      allRostersSelectedRef.current = false;
    } else {
      // If no roster contains today, fall back to published or latest roster
      const sortedRosters = [...rosters].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA; // Newest first
      });

      const published = sortedRosters.find((r) => r.status === 'published');
      if (published) {
        setSelectedRosterId(published.id);
        allRostersSelectedRef.current = false;
      } else if (sortedRosters.length > 0) {
        setSelectedRosterId(sortedRosters[0].id);
        allRostersSelectedRef.current = false;
      }
    }

    // Auto-select today's date
    setSelectedDay(todayIso);
    hasInitializedRef.current = true;
  }, [rosters, formatDateLocal]);

  // Auto-select roster when it changes (only if "All Rosters" was not explicitly selected)
  // This handles cases where rosters are added/removed after initial load
  useEffect(() => {
    // Only auto-select if not explicitly set to "All Rosters" and no roster is selected
    // Skip if we've already initialized
    if (hasInitializedRef.current) return;

    if (!allRostersSelectedRef.current && !selectedRosterId && rosters.length > 0) {
      // Sort rosters by creation date (newest first) to get the latest one
      const sortedRosters = [...rosters].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA; // Newest first
      });

      // Prefer published roster, but if none, use the latest roster
      const published = sortedRosters.find((r) => r.status === 'published');
      if (published) {
        setSelectedRosterId(published.id);
        allRostersSelectedRef.current = false;
      } else {
        // Select the latest roster (first in sorted array)
        setSelectedRosterId(sortedRosters[0].id);
        allRostersSelectedRef.current = false;
      }
    }
  }, [rosters, selectedRosterId]);

  // Fetch slots for active roster and current month only
  const { data: slotsResponse, isLoading: slotsLoading } = useShiftSlots(
    activeRoster
      ? {
          rosterId: activeRoster.id,
          startDate: monthDateRange.startDate,
          endDate: monthDateRange.endDate,
        }
      : {
          startDate: monthDateRange.startDate,
          endDate: monthDateRange.endDate,
        },
  );

  // Fetch slot counts per roster (only when roster list changes, not per month)
  // This could be optimized further with a separate count endpoint
  const { data: allSlotsResponse } = useShiftSlots(
    undefined, // Fetch all for counts - could be optimized with a count-only endpoint
  );

  const slots = useMemo(() => {
    // API returns array directly
    const result = (slotsResponse || []) as ShiftSlotWithUser[];
    return result;
  }, [slotsResponse]);

  // Calculate slot counts per roster from all slots
  const slotCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    // API returns array directly
    const allSlots = (allSlotsResponse || []) as ShiftSlotWithUser[];
    for (const roster of rosters) {
      const rosterSlots = allSlots.filter((s) => s.rosterId === roster.id);
      counts[roster.id] = rosterSlots.length;
    }
    return counts;
  }, [rosters, allSlotsResponse]);

  // Persist functions
  const persistHolidays = (next: string[]) => {
    setHolidays(next);
    try {
      localStorage.setItem('holidays', JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const addHolidayForDate = (iso: string) => {
    if (!iso || holidays.includes(iso)) return false;
    const next = [...holidays, iso].sort();
    persistHolidays(next);
    return true;
  };

  const removeHolidayForDate = (iso: string) => {
    if (!iso || !holidays.includes(iso)) return false;
    const next = holidays.filter((day) => day !== iso);
    persistHolidays(next);
    return true;
  };

  const handleGenerate = async (data: {
    cycleLengthDays: number;
    startsOn: string;
    patternId: string | null;
    holidays: string[];
  }) => {
    // Only delete draft rosters, never published ones
    const draftRosters = rosters.filter((r) => r.status === 'draft');
    for (const draft of draftRosters) {
      try {
        await deleteRoster.mutateAsync(draft.id);
      } catch (error) {
        console.error(`Failed to delete draft roster ${draft.id}:`, error);
      }
    }

    // Calculate end date from start date + cycle length
    const startDate = new Date(data.startsOn);
    const endDate = new Date(startDate.getTime() + (data.cycleLengthDays - 1) * 24 * 3600 * 1000);

    const result = await generateRoster.mutateAsync({
      startsOn: startDate.toISOString(),
      endsOn: endDate.toISOString(),
      patternId: data.patternId,
      holidays: data.holidays,
      findFirstEmptyDay: true, // Find first empty day after published rosters
    });

    // Auto-select the newly generated roster and exit "All Rosters" mode
    if (result.id) {
      allRostersSelectedRef.current = false;
      setSelectedRosterId(result.id);
    }

    const totalSlots = result.slots?.length ?? 0;
    if (totalSlots === 0) {
      toast.warning('Roster generated but no slots created', {
        description: 'Users may need group assignments in User Management.',
        duration: 8000,
      });
    } else {
      toast.success('Roster generated', {
        description: `Created ${totalSlots} slots`,
      });
    }
  };

  const handleDeleteRoster = async (rosterId: string) => {
    const rosterToDelete = rosters.find((r) => r.id === rosterId);
    const isPublished = rosterToDelete?.status === 'published';

    await deleteRoster.mutateAsync(rosterId);

    // Show appropriate toast message
    if (isPublished) {
      toast.success('Roster unpublished and deleted', {
        description: `"${rosterToDelete?.name}" has been unpublished and deleted along with all associated shift slots.`,
      });
    } else {
      toast.success('Roster deleted', {
        description: `"${rosterToDelete?.name}" has been deleted along with all associated shift slots.`,
      });
    }

    // Clear selection if deleted roster was selected and trigger a refresh of the slots
    if (selectedRosterId === rosterId) {
      const remaining = rosters.filter((r) => r.id !== rosterId);
      if (remaining.length > 0) {
        const published = remaining.find((r) => r.status === 'published');
        allRostersSelectedRef.current = false;
        setSelectedRosterId(published?.id || remaining[remaining.length - 1].id);
        // Trigger a refresh of the slots
      } else {
        allRostersSelectedRef.current = true;
        setSelectedRosterId(null);
      }
    }
  };

  const handlePublish = async (rosterId?: string) => {
    const rosterToPublish = rosterId ? rosters.find((r) => r.id === rosterId) : activeRoster;
    if (!rosterToPublish) return;

    await updateRoster.mutateAsync({
      id: rosterToPublish.id,
      data: { status: 'published' },
    });

    // Update selection to the published roster
    allRostersSelectedRef.current = false;
    setSelectedRosterId(rosterToPublish.id);
  };

  const handleAddShift = async (data: { userId: string; shiftId: string }) => {
    if (!activeRoster || !addShiftDate || !data.userId || !data.shiftId) {
      toast.error('Please select a specific roster to add shifts');
      return;
    }

    const selectedUser = users.find((u) => u.id === data.userId);
    const selectedShift = shifts.find((sh: any) => sh.id === data.shiftId);

    if (!selectedUser || !selectedShift) return;

    // Look up the user's actual group assignment
    const userGroupEntry = userGroups.find((ug: any) => ug.userId === data.userId);
    const groupId = (userGroupEntry as any)?.groupId || '';

    await createSlot.mutateAsync({
      rosterId: activeRoster.id,
      groupId,
      userId: data.userId,
      shiftId: data.shiftId,
      shiftDate: new Date(addShiftDate).toISOString() as any,
      startTime: (selectedShift as any).startTime,
      endTime: (selectedShift as any).endTime,
      status: 'planned',
    });

    setAddShiftOpen(false);
  };

  const openEdit = (slot: ShiftSlotWithUser) => {
    setEditingSlot(slot);
    // Use the slot's shiftId directly; fall back to time-matching only if shiftId is absent
    const shiftId =
      (slot as any).shiftId ||
      (
        shifts.find(
          (sh: any) => slot.startTime === sh.startTime && slot.endTime === sh.endTime,
        ) as any
      )?.id ||
      '';
    setEditingShiftId(shiftId);
    setEditingUserId(slot.userId ?? '');
    setEditOpen(true);
  };

  const applyEdit = async () => {
    if (!editingSlot) return;

    if (editingShiftId === 'null') {
      // Day off: clear shift but keep the slot
      await updateSlot.mutateAsync({
        id: editingSlot.id,
        data: {
          shiftId: null,
          userId: editingUserId || null,
          startTime: null,
          endTime: null,
          status: 'planned',
        },
      });
    } else if (editingShiftId) {
      const sh = shifts.find((x: any) => x.id === editingShiftId);
      if (sh) {
        await updateSlot.mutateAsync({
          id: editingSlot.id,
          data: {
            shiftId: (sh as any).id,
            userId: editingUserId || null,
            startTime: (sh as any).startTime,
            endTime: (sh as any).endTime,
            status: 'planned',
          },
        });
      }
    }

    setEditOpen(false);
    setEditingSlot(null);
    setEditingShiftId('');
    setEditingUserId('');
  };

  const applyDelete = async () => {
    if (!editingSlot) return;
    await deleteSlot.mutateAsync(editingSlot.id);
    setEditOpen(false);
    setEditingSlot(null);
    setEditingShiftId('');
    setEditingUserId('');
  };

  const weekView = useMemo(() => {
    if (!slots.length) return [];
    const byDate: Record<string, ShiftSlotWithUser[]> = {};
    for (const s of slots) {
      // Use UTC date string to stay consistent with how dates are stored on the server
      const d = new Date(s.shiftDate).toISOString().slice(0, 10);
      if (!byDate[d]) byDate[d] = [];
      byDate[d].push(s);
    }
    const dates = Object.keys(byDate).sort();
    return dates.map((d: any) => ({ date: d, items: byDate[d] }));
  }, [slots]);

  const monthView = useMemo(() => {
    const year = monthCursor.getFullYear();
    const month = String(monthCursor.getMonth() + 1).padStart(2, '0');
    const prefix = `${year}-${month}-`;
    return weekView.filter(({ date }: any) => date.startsWith(prefix));
  }, [weekView, monthCursor]);

  // Get slots for selected day
  const selectedDaySlots = useMemo(() => {
    if (!selectedDay) return [];
    return slots.filter((s) => {
      const slotDate = new Date(s.shiftDate).toISOString().slice(0, 10);
      return slotDate === selectedDay;
    });
  }, [slots, selectedDay]);

  const isLoading =
    groupsLoading ||
    shiftsLoading ||
    patternsLoading ||
    usersLoading ||
    rostersLoading ||
    slotsLoading;

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-balance">
            {t('shiftManagement.title')}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {t('shiftManagement.description')}
          </p>
        </div>
        <div>
          <Tabs defaultValue="calendar">
            <TabsList>
              <TabsTrigger value="calendar">{t('shiftManagement.tabs.calendar')}</TabsTrigger>
              <TabsTrigger
                value="configuration"
                onClick={() => {
                  navigate({
                    to: '/user-management',
                    search: { tab: 'users', subTab: 'shifts' },
                    replace: true,
                  });
                }}
              >
                {t('shiftManagement.tabs.configuration')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-8">
        {/* Shift Configuration Panel */}
        <div className="flex flex-col gap-4 md:col-span-2 lg:col-span-3 max-h-[calc(100vh-10rem)] overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground scrollbar-track-muted-foreground/20">
          <DayDetail
            selectedDay={selectedDay}
            slots={selectedDaySlots}
            groups={groups}
            holidays={holidays}
            activeRoster={activeRoster}
            onToggleHoliday={(date) => {
              if (holidays.includes(date)) {
                removeHolidayForDate(date);
              } else {
                addHolidayForDate(date);
              }
            }}
            onAddShift={(date) => {
              setAddShiftDate(date);
              setAddShiftOpen(true);
            }}
            onEditSlot={openEdit}
            onDeleteSlot={applyDelete}
            onClose={() => setSelectedDay(null)}
          />
          <ShiftConfigurationPanel
            rosters={rosters}
            selectedRosterId={selectedRosterId}
            onSelectRoster={(rosterId) => {
              if (rosterId === null) {
                allRostersSelectedRef.current = true;
                setSelectedRosterId(null);
              } else {
                allRostersSelectedRef.current = false;
                setSelectedRosterId(rosterId);
              }
            }}
            onDeleteRoster={handleDeleteRoster}
            slotCounts={slotCounts}
            isDeleting={deleteRoster.isPending}
            rostersLoading={rostersLoading}
            groups={groups}
            shifts={shifts}
            isLoading={isLoading}
            patterns={patterns}
            selectedPatternId={selectedPatternId}
            onPatternChange={setSelectedPatternId}
            onGenerate={handleGenerate}
            onPublish={handlePublish}
            holidays={holidays}
            newHoliday={newHoliday}
            onNewHolidayChange={setNewHoliday}
            onAddHoliday={(date) => {
              if (addHolidayForDate(date)) {
                setNewHoliday('');
              }
            }}
            isGenerating={generateRoster.isPending}
            isPublishing={updateRoster.isPending}
          />
        </div>

        {/* Roster View */}
        <div className="md:col-span-2 lg:col-span-5">
          <CalendarView
            monthCursor={monthCursor}
            onMonthChange={setMonthCursor}
            monthView={monthView}
            holidays={holidays}
            groups={groups}
            shifts={shifts}
            selectedDay={selectedDay}
            onDaySelect={setSelectedDay}
            onDayPopoverChange={setOpenDayPopover}
            openDayPopover={openDayPopover}
            activeRoster={activeRoster}
            onAddHoliday={addHolidayForDate}
            onRemoveHoliday={removeHolidayForDate}
            onAddShift={(date) => {
              setAddShiftDate(date);
              setAddShiftOpen(true);
            }}
          />
        </div>
      </div>

      {/* Edit Slot Dialog */}
      <EditSlotDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        slot={editingSlot}
        users={users}
        shifts={shifts}
        editingShiftId={editingShiftId}
        editingUserId={editingUserId}
        onShiftIdChange={setEditingShiftId}
        onUserIdChange={setEditingUserId}
        onSave={applyEdit}
        onDelete={applyDelete}
        isSaving={updateSlot.isPending}
        isDeleting={deleteSlot.isPending}
      />

      {/* Add Shift Dialog */}
      <AddShiftDialog
        open={addShiftOpen}
        onOpenChange={setAddShiftOpen}
        date={addShiftDate}
        users={users}
        shifts={shifts}
        onSubmit={handleAddShift}
        isSubmitting={createSlot.isPending}
      />
    </div>
  );
}
